<div class="w-full">
  <div class="px-6 pt-6">
    <h2 class="text-lg font-semibold text-slate-900">{{ 'tariff.version.wizard.title' | translate }}</h2>
    <p class="mt-1 text-sm text-slate-500">{{ 'tariff.version.wizard.subtitle' | translate: { name: data.tariff.name } }}</p>
  </div>

  <mat-stepper [linear]="false" #stepper class="tariff-rule-stepper">
    <!-- Step 1: Validity -->
    <mat-step [completed]="!!validityForm.value.validFrom">
      <ng-template matStepLabel>{{ 'tariff.version.step.validity' | translate }}</ng-template>
      <form [formGroup]="validityForm" class="min-h-[240px] space-y-4 px-4 py-4">
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'tariff.version.validFrom' | translate }}</mat-label>
          <input matInput [matDatepicker]="fromPicker" formControlName="validFrom">
          <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
          <mat-datepicker #fromPicker></mat-datepicker>
        </mat-form-field>
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'tariff.version.validTo' | translate }}</mat-label>
          <input matInput [matDatepicker]="toPicker" formControlName="validTo">
          <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
          <mat-datepicker #toPicker></mat-datepicker>
        </mat-form-field>
        <p class="text-xs text-slate-400">{{ 'tariff.version.validToHint' | translate }}</p>
      </form>
      <div class="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
        <button type="button" (click)="cancel()" class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{{ 'tariff.rule.cancel' | translate }}</button>
        <button type="button" (click)="stepper.next()" [disabled]="validityForm.invalid" class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{{ 'tariff.rule.next' | translate }}</button>
      </div>
    </mat-step>

    <!-- Step 2: Pricing basics -->
    <mat-step [completed]="pricingForm.valid">
      <ng-template matStepLabel>{{ 'tariff.version.step.pricing' | translate }}</ng-template>
      <form [formGroup]="pricingForm" class="min-h-[280px] space-y-4 px-4 py-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="tv-amount">{{ 'tariff.version.baseAmount' | translate }}</label>
            <input id="tv-amount" type="number" min="0" step="0.01" formControlName="baseAmount"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="tv-currency">{{ 'tariff.form.currency' | translate }}</label>
            <input id="tv-currency" type="text" maxlength="3" formControlName="baseCurrency"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm uppercase text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700" for="tv-timebasis">{{ 'tariff.version.timeBasis' | translate }}</label>
          <select id="tv-timebasis" formControlName="timeBasis"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
            @for (tb of timeSeriesTypes; track tb) { <option [value]="tb">{{ tb }}</option> }
          </select>
          <p class="mt-1 text-xs text-slate-400">{{ 'tariff.version.timeBasisHint' | translate }}</p>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="tv-interval">{{ 'tariff.version.interval' | translate }}</label>
            <select id="tv-interval" formControlName="interval"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              @for (i of billingIntervals; track i) { <option [value]="i">{{ i }}</option> }
            </select>
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="tv-intervalcount">{{ 'tariff.version.intervalCount' | translate }}</label>
            <input id="tv-intervalcount" type="number" min="1" formControlName="intervalCount"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700" for="tv-allowedentries">{{ 'tariff.version.allowedEntries' | translate }}</label>
          <input id="tv-allowedentries" type="number" min="0" step="1" formControlName="allowedEntries"
            class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
        </div>
        <p class="text-xs text-slate-400">{{ 'tariff.version.pricingHint' | translate }}</p>
      </form>
      <div class="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
        <button type="button" (click)="stepper.previous()" class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{{ 'tariff.rule.back' | translate }}</button>
        <button type="button" (click)="stepper.next()" [disabled]="pricingForm.invalid" class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{{ 'tariff.rule.next' | translate }}</button>
      </div>
    </mat-step>

    <!-- Step 3: Grace periods -->
    <mat-step [completed]="stagedGracePeriods().length > 0">
      <ng-template matStepLabel>{{ 'tariff.version.step.grace' | translate }}</ng-template>
      <div class="min-h-[280px] space-y-4 px-4 py-4">
        <p class="text-sm text-slate-500">{{ 'tariff.version.grace.hint' | translate }}</p>

        @if (stagedGracePeriods().length > 0) {
          <ul class="space-y-2">
            @for (g of stagedGracePeriods(); track $index) {
              <li class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <span>{{ g.durationMinutes }} {{ 'tariff.version.grace.minutes' | translate }} · {{ g.trigger }} · {{ g.behavior }}</span>
                <button type="button" (click)="removeGracePeriod($index)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate" class="text-slate-400 hover:text-red-600">
                  <mat-icon class="!h-4 !w-4 !text-[16px]">close</mat-icon>
                </button>
              </li>
            }
          </ul>
        }

        <form [formGroup]="gracePeriodDraft" class="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-300 p-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.grace.duration' | translate }}</label>
            <input type="number" min="1" formControlName="durationMinutes" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.grace.trigger' | translate }}</label>
            <select formControlName="trigger" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              @for (tr of graceTriggers; track tr) { <option [value]="tr">{{ tr }}</option> }
            </select>
          </div>
          <div class="col-span-2">
            <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.grace.behavior' | translate }}</label>
            <select formControlName="behavior" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              @for (b of graceBehaviors; track b) { <option [value]="b">{{ b }}</option> }
            </select>
          </div>
          <div class="col-span-2 flex justify-end">
            <button type="button" (click)="addGracePeriod()" class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
              {{ 'tariff.version.grace.add' | translate }}
            </button>
          </div>
        </form>
      </div>
      <div class="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
        <button type="button" (click)="stepper.previous()" class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{{ 'tariff.rule.back' | translate }}</button>
        <button type="button" (click)="stepper.next()" class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">{{ 'tariff.rule.next' | translate }}</button>
      </div>
    </mat-step>

    <!-- Step 4: Rate tiers and caps (Complex Pricing) -->
    <mat-step>
      <ng-template matStepLabel>{{ 'tariff.version.step.complexPricing' | translate }}</ng-template>
      <div class="min-h-[280px] space-y-5 px-4 py-4">
        <p class="text-sm text-slate-500">{{ 'tariff.version.complexPricing.body' | translate }}</p>

        <!-- Time-of-day rate bands -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">{{ 'tariff.rateBands' | translate }}</h4>

          @if (stagedRateBands().length > 0) {
            <ul class="space-y-2">
              @for (b of stagedRateBands(); track $index) {
                <li class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <span>{{ b.startTime }}–{{ b.endTime }} → {{ b.amount }} {{ pricingForm.value.baseCurrency }}</span>
                  <button type="button" (click)="removeRateBand($index)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate" class="text-slate-400 hover:text-red-600">
                    <mat-icon class="!h-4 !w-4 !text-[16px]">close</mat-icon>
                  </button>
                </li>
              }
            </ul>
          }

          <form [formGroup]="rateBandDraft" class="grid grid-cols-3 gap-3 rounded-lg border border-dashed border-slate-300 p-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.bandFrom' | translate }}</label>
              <input type="time" formControlName="startTime" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.bandTo' | translate }}</label>
              <input type="time" formControlName="endTime" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.bandAmount' | translate }} ({{ pricingForm.value.baseCurrency }})</label>
              <input type="number" min="0" step="0.01" formControlName="amount" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            <div class="col-span-3 grid grid-cols-3 items-end gap-3">
              <div class="col-span-2">
                <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.timeZone' | translate }}</label>
                <input type="text" [formControl]="timeZoneId" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
              </div>
              <div class="flex justify-end">
                <button type="button" (click)="addRateBand()" class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
                  {{ 'tariff.version.complexPricing.addBand' | translate }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Rate tiers -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">{{ 'tariff.rateTiers' | translate }}</h4>

          @if (stagedRateTiers().length > 0) {
            <ul class="space-y-2">
              @for (t of stagedRateTiers(); track $index) {
                <li class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <span>{{ 'tariff.after' | translate }} {{ t.afterMinutes }} {{ 'tariff.version.grace.minutes' | translate }} → {{ t.newAmount }} {{ pricingForm.value.baseCurrency }}</span>
                  <button type="button" (click)="removeRateTier($index)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate" class="text-slate-400 hover:text-red-600">
                    <mat-icon class="!h-4 !w-4 !text-[16px]">close</mat-icon>
                  </button>
                </li>
              }
            </ul>
          }

          <form [formGroup]="rateTierDraft" class="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-300 p-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.afterMinutes' | translate }}</label>
              <input type="number" min="1" formControlName="afterMinutes" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.newAmount' | translate }} ({{ pricingForm.value.baseCurrency }})</label>
              <input type="number" min="0" step="0.01" formControlName="newAmount" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            <div class="col-span-2 flex items-center justify-between">
              <p class="text-xs text-slate-400">{{ 'tariff.version.complexPricing.tierHint' | translate }}</p>
              <button type="button" (click)="addRateTier()" class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
                {{ 'tariff.version.complexPricing.addTier' | translate }}
              </button>
            </div>
          </form>
        </div>

        <!-- Rate caps -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">{{ 'tariff.caps' | translate }}</h4>

          @if (stagedRateCaps().length > 0) {
            <ul class="space-y-2">
              @for (c of stagedRateCaps(); track $index) {
                <li class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <span>{{ c.window }} → {{ 'tariff.max' | translate }} {{ c.maxAmount }} {{ pricingForm.value.baseCurrency }}</span>
                  <button type="button" (click)="removeRateCap($index)" [attr.aria-label]="'tariff.rule.conditions.remove' | translate" class="text-slate-400 hover:text-red-600">
                    <mat-icon class="!h-4 !w-4 !text-[16px]">close</mat-icon>
                  </button>
                </li>
              }
            </ul>
          }

          <form [formGroup]="rateCapDraft" class="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-300 p-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.window' | translate }}</label>
              <select formControlName="window" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                @for (w of capWindows; track w) { <option [value]="w">{{ w }}</option> }
              </select>
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-slate-600">{{ 'tariff.version.complexPricing.maxAmount' | translate }} ({{ pricingForm.value.baseCurrency }})</label>
              <input type="number" min="0" step="0.01" formControlName="maxAmount" class="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            <div class="col-span-2 flex items-center justify-between">
              <p class="text-xs text-slate-400">{{ 'tariff.version.complexPricing.capHint' | translate }}</p>
              <button type="button" (click)="addRateCap()" class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
                {{ 'tariff.version.complexPricing.addCap' | translate }}
              </button>
            </div>
          </form>
        </div>

        @if (complexPricingError()) {
          <div class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{{ complexPricingError() }}</div>
        }

        <div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {{ 'tariff.version.complexPricing.immutableHint' | translate }}
        </div>
      </div>
      <div class="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
        <button type="button" (click)="stepper.previous()" class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{{ 'tariff.rule.back' | translate }}</button>
        <button type="button" (click)="stepper.next()" class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">{{ 'tariff.rule.next' | translate }}</button>
      </div>
    </mat-step>

    <!-- Step 5: Summary -->
    <mat-step>
      <ng-template matStepLabel>{{ 'tariff.rule.step.summary' | translate }}</ng-template>
      <div class="min-h-[280px] space-y-4 px-4 py-4">
        <dl class="divide-y divide-slate-100 rounded-lg border border-slate-200">
          <div class="flex justify-between px-3 py-2 text-sm"><dt class="text-slate-500">{{ 'tariff.version.validFrom' | translate }}</dt><dd class="font-medium text-slate-800">{{ validityForm.value.validFrom | date }}</dd></div>
          <div class="flex justify-between px-3 py-2 text-sm"><dt class="text-slate-500">{{ 'tariff.version.validTo' | translate }}</dt><dd class="font-medium text-slate-800">{{ validityForm.value.validTo ? (validityForm.value.validTo | date) : ('tariff.version.open' | translate) }}</dd></div>
          <div class="flex justify-between px-3 py-2 text-sm"><dt class="text-slate-500">{{ 'tariff.version.baseAmount' | translate }}</dt><dd class="font-medium text-slate-800">{{ pricingForm.value.baseAmount }} {{ pricingForm.value.baseCurrency }}</dd></div>
          <div class="flex justify-between px-3 py-2 text-sm"><dt class="text-slate-500">{{ 'tariff.version.timeBasis' | translate }}</dt><dd class="font-medium text-slate-800">{{ pricingForm.value.timeBasis }}</dd></div>
          <div class="flex justify-between px-3 py-2 text-sm"><dt class="text-slate-500">{{ 'tariff.version.interval' | translate }}</dt><dd class="font-medium text-slate-800">{{ pricingForm.value.intervalCount }} × {{ pricingForm.value.interval }}</dd></div>
          <div class="flex justify-between px-3 py-2 text-sm"><dt class="text-slate-500">{{ 'tariff.version.allowedEntries' | translate }}</dt><dd class="font-medium text-slate-800">{{ entriesLabel(pricingForm.value.allowedEntries) }}</dd></div>
        </dl>

        @if (stagedGracePeriods().length > 0) {
          <div>
            <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">{{ 'tariff.version.step.grace' | translate }}</h4>
            <ul class="mt-2 space-y-1 text-sm text-slate-600">
              @for (g of stagedGracePeriods(); track $index) {
                <li>{{ g.durationMinutes }} {{ 'tariff.version.grace.minutes' | translate }} · {{ g.trigger }} · {{ g.behavior }}</li>
              }
            </ul>
          </div>
        }

        @if (stagedRateTiers().length > 0 || stagedRateCaps().length > 0 || stagedRateBands().length > 0) {
          <div>
            <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">{{ 'tariff.version.step.complexPricing' | translate }}</h4>
            <ul class="mt-2 space-y-1 text-sm text-slate-600">
              @for (b of stagedRateBands(); track $index) {
                <li>{{ b.startTime }}–{{ b.endTime }} ({{ timeZoneId.value }}) → {{ b.amount }} {{ pricingForm.value.baseCurrency }}</li>
              }
              @for (t of stagedRateTiers(); track $index) {
                <li>{{ 'tariff.after' | translate }} {{ t.afterMinutes }} {{ 'tariff.version.grace.minutes' | translate }} → {{ t.newAmount }} {{ pricingForm.value.baseCurrency }}</li>
              }
              @for (c of stagedRateCaps(); track $index) {
                <li>{{ c.window }} → {{ 'tariff.max' | translate }} {{ c.maxAmount }} {{ pricingForm.value.baseCurrency }}</li>
              }
            </ul>
          </div>
        }

        @if (submitError()) {
          <div class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{{ submitError() }}</div>
        }
      </div>
      <div class="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
        <button type="button" (click)="stepper.previous()" [disabled]="isSubmitting()" class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40">{{ 'tariff.rule.back' | translate }}</button>
        <button type="button" (click)="submit()" [disabled]="isSubmitting()"
          class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
          {{ (isSubmitting() ? 'tariff.rule.submitting' : 'tariff.version.create') | translate }}
        </button>
      </div>
    </mat-step>
  </mat-stepper>
</div>
